import { ACTIVE_RESEARCH_STATUSES } from "../researchStatuses";
import type { ResearchSession } from "@app/queries/research";
import { useResearchSessions } from "@app/queries/research";
import { useState } from "react";

/**
 * The runs the chat status bar shows for this experiment, newest first: every run in flight, plus
 * the ones that succeeded while it was watching, so each can flip to Done. A run that finished
 * before this hook was watching is never returned, or every page load would resurrect the bar for
 * a long-finished run. `dismiss` drops a finished run — nothing else ever would — and cannot hide
 * one still in flight, which is re-adopted on the next render.
 */
export function useChatStatusSessions(
  experimentId: string,
  enabled: boolean
): { sessions: ResearchSession[]; dismiss: (sessionId: string) => void } {
  const listed = useResearchSessions(experimentId, enabled).data ?? [];
  const active = listed.filter(session => ACTIVE_RESEARCH_STATUSES.includes(session.status));

  // Set during render, not in an effect, so a run is watched in the pass it appears in.
  const [watched, setWatched] = useState<{ experimentId: string; sessionIds: string[] }>({
    experimentId,
    sessionIds: []
  });
  // The chat survives navigation between experiments, so watched runs count only for their own.
  const watchedIds = watched.experimentId === experimentId ? watched.sessionIds : [];
  const unwatched = active.filter(session => !watchedIds.includes(session.id));
  if (unwatched.length > 0 || watched.experimentId !== experimentId) {
    setWatched({ experimentId, sessionIds: [...watchedIds, ...unwatched.map(session => session.id)] });
  }

  const dismiss = (sessionId: string) =>
    setWatched(previous => ({ ...previous, sessionIds: previous.sessionIds.filter(id => id !== sessionId) }));

  const shown = listed.filter(
    session =>
      ACTIVE_RESEARCH_STATUSES.includes(session.status) ||
      (session.status === "SUCCEEDED" && watchedIds.includes(session.id))
  );
  // The API already lists newest first; sorting here keeps that true of the stack regardless.
  const sessions = [...shown].sort((a, b) => b.created_at.localeCompare(a.created_at));

  return { sessions, dismiss };
}
